import { Form, Formik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { FiArchive, FiLayers, FiPackage, FiPlus } from 'react-icons/fi';
import { useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import AgingProcessSelector, { AgingProcessOption } from '../components/AgingProcessSelector';
import ErrorMessageComponent from '../components/ErrorMessage';
import LoadingSpinner from '../components/LoadingSpinner';
import MobileModal from '../components/MobileModal';
import { PermissionButton } from '../components/PermissionButton';
import { Alert, ConfirmDialog, FormActions, FormDateInput, FormField, FormSelect, FormTextarea, PageHeader, StatCard } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import {
  AGING_COMPLETED_CONFIRM_QUESTION,
  AGING_COMPLETED_CONFIRM_TITLE,
  AGING_MUST_COMPLETE_FOR_BOTTLING,
  completeAgingForBottling,
} from '../utils/completeAgingForBottling';

interface BottlingRun {
  id: number;
  batchId: number;
  bottlingDate: string;
  bottleSize: string;
  bottleCount: number;
  lotNumber: string;
  responsibleUserId: number;
  status: string;
  labelInfo?: any;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  batchNumber?: string;
  agingId?: number;
  aging?: {
    id: number;
    startDate: string;
    endDate?: string;
    vesselId: number;
    vessel?: {
      id: number;
      name: string;
      type: string;
    };
    batch?: {
      id: number;
      batch_id: string;
      batch_name: string;
      wine_type: string;
      vineyard_id?: number;
      grape_variety?: string;
      vineyard?: {
        id: number;
        name: string;
      };
    };
  };
}

interface BottlingFormValues {
  agingId: number;
  batchId: number;
  bottlingDate: string;
  bottleSize: string;
  bottleCount: number;
  lotNumber: string;
  responsibleUserId: number;
  status?: string;
  labelInfo?: any;
  notes?: string;
}

const BottlingSchema = Yup.object().shape({
  agingId: Yup.number().required('Aging process is required').min(1, 'Select an aging process'),
  bottlingDate: Yup.date().required('Bottling date is required'),
  bottleSize: Yup.string().required('Bottle size is required'),
  bottleCount: Yup.number().required('Quantity is required').positive('Quantity must be positive'),
  lotNumber: Yup.string().required('Lot number is required'),
  responsibleUserId: Yup.number().required('Responsible user is required'),
  status: Yup.string().oneOf(['Planned', 'Completed', 'Cancelled']),
  notes: Yup.string()
});

const bottleSizes = [
  { value: '375ml', label: '375ml (Half)' },
  { value: '750ml', label: '750ml (Standard)' },
  { value: '1.5L', label: '1.5L (Magnum)' },
  { value: '3L', label: '3L (Double Magnum)' },
  { value: '5L', label: '5L (Jeroboam)' }
];

/** Wine type to Tailwind color classes (Red → red, White → yellow, Rosé → pink, Orange → orange) */
const getWineTypeTagClasses = (wineType: string | undefined | null): string => {
  if (!wineType) return 'bg-neutral-100 text-neutral-700';
  const t = String(wineType).trim().toLowerCase();
  if (t === 'red') return 'bg-red-100 text-red-700';
  if (t === 'white') return 'bg-yellow-100 text-yellow-700';
  if (t === 'rosé' || t === 'rose') return 'bg-pink-100 text-pink-700';
  if (t === 'orange') return 'bg-orange-100 text-orange-700';
  return 'bg-neutral-100 text-neutral-700';
};

// Helper function for safer date formatting
const formatDate = (dateString: string | undefined | null): string => {
  if (!dateString) return 'N/A';
  try {
    // First, try to clean the date string if needed
    let cleanDateStr = dateString;

    // Handle timezone issues by ensuring we're working with a clean YYYY-MM-DD format
    if (dateString.includes('T')) {
      cleanDateStr = dateString.split('T')[0];
    }

    // Try to create a valid date object
    const date = new Date(cleanDateStr);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.error("Invalid date detected:", dateString);
      return 'Invalid Date';
    }

    // Use a more standard, unambiguous format
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (error) {
    console.error("Error formatting date:", dateString, error);
    return 'Invalid Date';
  }
};

const Bottling: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = (user?.Role?.name || (user as any)?.role?.name || '').toLowerCase() === 'admin';
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkHandledRef = useRef(false);
  const [bottlingRuns, setBottlingRuns] = useState<BottlingRun[]>([]);
  const [wineBatches, setWineBatches] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingBottling, setEditingBottling] = useState<BottlingRun | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; bottlingId?: number }>({ open: false });
  const [totalBottles, setTotalBottles] = useState<number>(0);
  const [selectedAgingData, setSelectedAgingData] = useState<AgingProcessOption | null>(null);
  const [prefillAgingId, setPrefillAgingId] = useState<number>(0);
  const [creatingInventoryForId, setCreatingInventoryForId] = useState<number | null>(null);
  const [addToInventoryPrompt, setAddToInventoryPrompt] = useState<BottlingRun | null>(null);
  const [bottlingIdsInInventory, setBottlingIdsInInventory] = useState<Set<number>>(new Set());
  const [agingCompleteConfirm, setAgingCompleteConfirm] = useState<AgingProcessOption | null>(null);
  const [agingCompleteConfirmError, setAgingCompleteConfirmError] = useState<string | null>(null);
  const [agingCompleteConfirmLoading, setAgingCompleteConfirmLoading] = useState(false);
  const [agingConfirmMode, setAgingConfirmMode] = useState<'deeplink' | 'selector'>('deeplink');
  const [agingSelectorRefreshKey, setAgingSelectorRefreshKey] = useState(0);
  const applyAgingSelectionRef = useRef<((aging: AgingProcessOption) => void) | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [bottlingRes, batchesRes, usersRes, inventoryRes] = await Promise.all([
          api.get('/api/bottling'),
          api.get('/api/batch'),
          api.get('/api/users'),
          api.get('/api/inventory').catch(() => ({ data: [] }))
        ]);
        // Normalize runs so bottleCount is always set (API may return bottle_count)
        const runs = Array.isArray(bottlingRes.data) ? bottlingRes.data : [];
        const normalizedRuns: BottlingRun[] = runs.map((r: any) => ({
          ...r,
          bottleCount: r.bottleCount ?? r.bottle_count ?? 0,
          bottleSize: r.bottleSize ?? r.bottle_size ?? '',
          lotNumber: r.lotNumber ?? r.lot_number ?? '',
          responsibleUserId: r.responsibleUserId ?? r.responsible_user_id ?? 0,
          agingId: r.agingId ?? r.aging_id ?? 0,
          batchId: r.batchId ?? r.batch_id ?? r.aging?.batch_id ?? r.aging?.batch?.id ?? 0
        }));
        setBottlingRuns(normalizedRuns);
        setWineBatches(batchesRes.data);
        setUsers(usersRes.data);

        const inventory = Array.isArray(inventoryRes.data) ? inventoryRes.data : [];
        const ids = new Set(
          inventory
            .map((i: any) => i.bottlingId ?? i.bottling_id)
            .filter((id: unknown): id is number => typeof id === 'number')
        );
        setBottlingIdsInInventory(ids);

        // Calculate total bottles
        const total = normalizedRuns.reduce((sum: number, run: BottlingRun) =>
          sum + (run?.bottleCount ?? 0), 0);
        setTotalBottles(total);
      } catch (err: any) {
        console.error('Error fetching data:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Deep-link: /bottling?agingId=<id> opens create modal with that aging pre-selected
  useEffect(() => {
    if (loading || deepLinkHandledRef.current) return;
    const raw = searchParams.get('agingId');
    if (!raw) return;

    const agingId = Number(raw);
    if (!Number.isInteger(agingId) || agingId <= 0) {
      deepLinkHandledRef.current = true;
      const next = new URLSearchParams(searchParams);
      next.delete('agingId');
      setSearchParams(next, { replace: true });
      return;
    }

    deepLinkHandledRef.current = true;
    const applyDeepLink = async () => {
      try {
        const response = await api.get(`/api/aging/${agingId}`);
        const aging = response.data as AgingProcessOption;
        if (aging && String(aging.status) === 'Completed') {
          setSelectedAgingData(aging);
          setPrefillAgingId(aging.id);
          setEditingBottling(null);
          setIsModalOpen(true);
        } else if (aging) {
          setAgingConfirmMode('deeplink');
          setAgingCompleteConfirmError(null);
          setAgingCompleteConfirm(aging);
        }
      } catch (err: any) {
        console.error('Error loading aging for deep link:', err);
        setError(err);
      } finally {
        const next = new URLSearchParams(searchParams);
        next.delete('agingId');
        setSearchParams(next, { replace: true });
      }
    };
    void applyDeepLink();
  }, [loading, searchParams, setSearchParams]);

  const openModalWithAging = (aging: AgingProcessOption) => {
    setSelectedAgingData(aging);
    setPrefillAgingId(aging.id);
    setEditingBottling(null);
    setIsModalOpen(true);
  };

  const closeAgingCompleteConfirm = () => {
    setAgingCompleteConfirm(null);
    setAgingCompleteConfirmError(null);
    setAgingCompleteConfirmLoading(false);
    applyAgingSelectionRef.current = null;
  };

  const handleConfirmAgingCompletedForBottling = async () => {
    if (!agingCompleteConfirm) return;
    setAgingCompleteConfirmLoading(true);
    setAgingCompleteConfirmError(null);
    try {
      await completeAgingForBottling(agingCompleteConfirm);
      const endDate =
        agingCompleteConfirm.end_date ||
        agingCompleteConfirm.endDate ||
        new Date().toISOString().split('T')[0];
      const completedAging: AgingProcessOption = {
        ...agingCompleteConfirm,
        status: 'Completed',
        end_date: endDate,
        endDate,
      };
      const mode = agingConfirmMode;
      const applySelection = applyAgingSelectionRef.current;
      closeAgingCompleteConfirm();
      if (mode === 'selector') {
        applySelection?.(completedAging);
        setAgingSelectorRefreshKey((k) => k + 1);
      } else {
        openModalWithAging(completedAging);
      }
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to complete aging process.';
      setAgingCompleteConfirmError(message);
      setAgingCompleteConfirmLoading(false);
    }
  };

  const openModal = (bottling: BottlingRun | null = null) => {
    setEditingBottling(bottling);
    if (!bottling) {
      setPrefillAgingId(0);
      setSelectedAgingData(null);
    }
    setIsModalOpen(true);
  };

  const closeModal = (bottlingJustCompleted?: BottlingRun) => {
    setIsModalOpen(false);
    setEditingBottling(null);
    setSelectedAgingData(null);
    setPrefillAgingId(0);
    if (bottlingJustCompleted) {
      setAddToInventoryPrompt(bottlingJustCompleted);
    }
  };

  const handleDelete = async (id: number) => {
    if (!isAdmin) return;
    setConfirmDelete({ open: true, bottlingId: id });
  };

  const confirmDeleteBottling = async () => {
    if (!confirmDelete.bottlingId) return;
    try {
      await api.delete(`/api/bottling/${confirmDelete.bottlingId}`);
      setBottlingRuns(prev => prev.filter(r => r.id !== confirmDelete.bottlingId));
      setSuccessMessage('Bottling run deleted successfully');
      setConfirmDelete({ open: false });
    } catch (err: any) {
      setError(err);
      setConfirmDelete({ open: false });
    }
  };

  // Group bottling runs by batch
  const groupRuns = (runs: BottlingRun[]) => {
    const grouped: Record<number, BottlingRun[]> = {};

    runs.forEach(run => {
      if (!grouped[run.batchId]) {
        grouped[run.batchId] = [];
      }
      grouped[run.batchId].push(run);
    });

    return grouped;
  };

  const resolveFermentationId = async (batchId: number): Promise<number | null> => {
    try {
      const res = await api.get('/api/fermentation');
      const list = Array.isArray(res.data) ? res.data : [];
      const byBatch = list.find((f: any) => (f.batch_id ?? f.batchId) === batchId);
      return byBatch ? (byBatch.id ?? null) : null;
    } catch {
      return null;
    }
  };

  const handleSubmit = async (values: BottlingFormValues, { resetForm, setSubmitting }: any) => {
    try {
      let updatedBottling: BottlingRun;

      if (editingBottling) {
        const updatePayload = {
          agingId: values.agingId || editingBottling.agingId,
          batchId: values.batchId || editingBottling.batchId,
          bottlingDate: values.bottlingDate,
          bottleSize: values.bottleSize,
          bottleCount: values.bottleCount,
          lotNumber: values.lotNumber,
          responsibleUserId: values.responsibleUserId,
          status: values.status,
          labelInfo: values.labelInfo,
          notes: values.notes
        };
        const updateResponse = await api.put(`/api/bottling/${editingBottling.id}`, updatePayload);

        updatedBottling = {
          ...editingBottling,
          ...updateResponse.data,
          id: updateResponse.data.id,
          batchId: updateResponse.data.batchId ?? updateResponse.data.batch_id ?? editingBottling.batchId,
          bottlingDate: updateResponse.data.bottlingDate ?? updateResponse.data.bottling_date ?? editingBottling.bottlingDate,
          bottleSize: updateResponse.data.bottleSize ?? updateResponse.data.bottle_size ?? editingBottling.bottleSize,
          bottleCount: updateResponse.data.bottleCount ?? updateResponse.data.bottle_count ?? editingBottling.bottleCount,
          lotNumber: updateResponse.data.lotNumber ?? updateResponse.data.lot_number ?? editingBottling.lotNumber,
          responsibleUserId: updateResponse.data.responsibleUserId ?? updateResponse.data.responsible_user_id ?? editingBottling.responsibleUserId,
          status: updateResponse.data.status ?? editingBottling.status,
          labelInfo: updateResponse.data.labelInfo ?? updateResponse.data.label_info ?? editingBottling.labelInfo,
          notes: updateResponse.data.notes ?? editingBottling.notes,
          createdAt: updateResponse.data.createdAt ?? updateResponse.data.created_at ?? editingBottling.createdAt,
          updatedAt: updateResponse.data.updatedAt ?? updateResponse.data.updated_at ?? new Date().toISOString(),
          batchNumber: updateResponse.data.batchNumber ?? updateResponse.data.batch_number ?? editingBottling.batchNumber,
          agingId: updateResponse.data.agingId ?? updateResponse.data.aging_id ?? editingBottling.agingId,
          aging: updateResponse.data.aging ?? editingBottling.aging
        };

        setBottlingRuns(bottlingRuns.map(b => (b.id === editingBottling.id ? updatedBottling : b)));
        setSuccessMessage('Bottling run updated successfully!');
      } else {
        const batchId = selectedAgingData?.batch_id ?? selectedAgingData?.batch?.id ?? 0;
        let fermentationId = selectedAgingData?.previousFermentationId ?? null;
        if (!fermentationId && batchId) {
          fermentationId = await resolveFermentationId(batchId);
        }
        if (!fermentationId) {
          setError({ message: 'Could not determine fermentation for this aging process. Please ensure the batch has a completed fermentation.' });
          setSubmitting(false);
          return;
        }
        const createPayload = {
          agingId: values.agingId,
          fermentationId,
          batchId,
          bottlingDate: values.bottlingDate,
          bottleSize: values.bottleSize,
          bottleCount: values.bottleCount,
          lotNumber: values.lotNumber,
          responsibleUserId: values.responsibleUserId,
          status: values.status ?? 'Planned',
          labelInfo: values.labelInfo,
          notes: values.notes
        };
        const createResponse = await api.post('/api/bottling', createPayload);

        updatedBottling = {
          ...createResponse.data,
          id: createResponse.data.id,
          batchId: createResponse.data.batchId ?? createResponse.data.batch_id,
          bottlingDate: createResponse.data.bottlingDate ?? createResponse.data.bottling_date,
          bottleSize: createResponse.data.bottleSize ?? createResponse.data.bottle_size,
          bottleCount: createResponse.data.bottleCount ?? createResponse.data.bottle_count,
          lotNumber: createResponse.data.lotNumber ?? createResponse.data.lot_number,
          responsibleUserId: createResponse.data.responsibleUserId ?? createResponse.data.responsible_user_id,
          status: createResponse.data.status,
          labelInfo: createResponse.data.labelInfo ?? createResponse.data.label_info,
          notes: createResponse.data.notes,
          createdAt: createResponse.data.createdAt ?? createResponse.data.created_at ?? new Date().toISOString(),
          updatedAt: createResponse.data.updatedAt ?? createResponse.data.updated_at ?? new Date().toISOString(),
          batchNumber: createResponse.data.batchNumber ?? createResponse.data.batch_number,
          agingId: createResponse.data.agingId ?? createResponse.data.aging_id,
          aging: createResponse.data.aging
        };

        setBottlingRuns([updatedBottling, ...bottlingRuns]);
        setSuccessMessage('Bottling run created successfully!');
      }

      setSelectedAgingData(null);
      resetForm();
      // Show "Add to Inventory" popup only when editing, status was changed TO Completed, and update succeeded
      const wasEditAndStatusChangedToCompleted =
        editingBottling &&
        editingBottling.status !== 'Completed' &&
        values.status === 'Completed';
      const justCompleted = wasEditAndStatusChangedToCompleted ? updatedBottling : undefined;
      closeModal(justCompleted);

      const updatedRuns = editingBottling
        ? bottlingRuns.map(b => (b.id === editingBottling.id ? updatedBottling : b))
        : [updatedBottling, ...bottlingRuns];
      const total = updatedRuns.reduce((sum, run) => sum + (run.bottleCount || 0), 0);
      setTotalBottles(total);

      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('Error saving bottling run:', err);
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateInventory = async (bottling: BottlingRun) => {
    setAddToInventoryPrompt(null);
    setCreatingInventoryForId(bottling.id);
    try {
      await api.post('/api/inventory', {
        bottlingId: bottling.id,
        wineName: bottling.lotNumber ?? bottling.aging?.batch?.batch_name ?? bottling.batchNumber ?? `Batch ${bottling.batchId}`,
        wineType: bottling.aging?.batch?.wine_type ?? '',
        bottleSize: bottling.bottleSize,
        quantity: bottling.bottleCount,
        vineyardId: bottling.aging?.batch?.vineyard_id ?? null,
        vesselId: bottling.aging?.vesselId ?? null
      });
      setBottlingIdsInInventory(prev => new Set(prev).add(bottling.id));
      setSuccessMessage('Inventory record created successfully.');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('Error creating inventory:', err);
      setError(err);
    } finally {
      setCreatingInventoryForId(null);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  // Group bottling runs by batch
  const bottlingsByBatch: Record<number, BottlingRun[]> = groupRuns(bottlingRuns);

  return (
    <div className="space-y-7">
      <PageHeader
        title="Bottling Management"
        subtitle="Track and manage wine bottling runs."
        actions={(
          <PermissionButton
            resource="bottling"
            action="create"
            onClick={() => openModal()}
          >
            Add Bottling Run
          </PermissionButton>
        )}
      />

      {error && (
        <ErrorMessageComponent error={error} className="mt-4" />
      )}

      {successMessage && (
        <Alert
          type="success"
          message={successMessage}
          autoHide
          onClose={() => setSuccessMessage(null)}
          className="mt-4"
        />
      )}

      {/* Summary Stats */}
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard title="Total Runs" value={bottlingRuns.length} icon={FiArchive({ size: 20 })} color="info" />
        <StatCard title="Total Bottles" value={totalBottles.toLocaleString()} icon={FiPackage({ size: 20 })} color="success" />
        <StatCard title="Batches Bottled" value={Object.keys(bottlingsByBatch).length} icon={FiLayers({ size: 20 })} color="primary" />
      </div>

      {/* Bottling Runs by Batch - Table-based layout */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold text-monday-text">Bottling Runs by Batch</h2>
        <div className="mt-4 space-y-6">
          {Object.entries(bottlingsByBatch).map(([batchId, runs]) => {
            const batch = wineBatches.find(b => b.id === Number(batchId));
            const batchTotalBottles = runs.reduce((sum, run) => sum + (run?.bottleCount ?? 0), 0);
            const wineType = batch?.wineType || batch?.wine_type;
            const firstEligibleRun = runs.find(
              (r) => r.status === 'Completed' && !bottlingIdsInInventory.has(r.id)
            );
            const hasAnyEligible = !!firstEligibleRun;
            const isAddingThisBatch = firstEligibleRun && creatingInventoryForId === firstEligibleRun.id;

            return (
              <section
                key={batchId}
                className="bg-white/95 overflow-hidden shadow rounded-xl border border-monday-border-light"
              >
                {/* Batch header: title + wine type + Add to Inventory (top right) */}
                <div className="px-4 py-4 sm:px-6 flex flex-wrap items-center justify-between gap-3 border-b border-monday-border-light bg-monday-background-subtle">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="text-lg font-semibold text-monday-text">
                      {batch?.batchName || batch?.batch_name || `Batch #${batchId}`}
                    </h3>
                    {wineType && (
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getWineTypeTagClasses(wineType)}`}
                      >
                        {wineType}
                      </span>
                    )}
                    <span className="text-sm text-monday-text-muted">
                      {(batchTotalBottles ?? 0).toLocaleString()} bottles total
                    </span>
                  </div>
                  {hasAnyEligible && (
                    <button
                      type="button"
                      onClick={() => firstEligibleRun && handleCreateInventory(firstEligibleRun)}
                      disabled={isAddingThisBatch}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-full bg-primary-600 text-white shadow-primary hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                    >
                      {React.createElement(FiPlus as React.ComponentType<React.SVGAttributes<SVGElement>>, { className: 'w-4 h-4', 'aria-hidden': true })}
                      Add to Inventory
                    </button>
                  )}
                </div>

                {/* Table of runs - scroll horizontally on small screens */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-monday-border-light">
                    <thead>
                      <tr className="bg-monday-background-subtle">
                        <th scope="col" className="py-3 pl-4 pr-3 text-left text-xs font-semibold text-monday-text-muted uppercase tracking-wider sm:pl-6">
                          Date
                        </th>
                        <th scope="col" className="py-3 px-3 text-left text-xs font-semibold text-monday-text-muted uppercase tracking-wider">
                          Lot
                        </th>
                        <th scope="col" className="py-3 px-3 text-left text-xs font-semibold text-monday-text-muted uppercase tracking-wider">
                          Size
                        </th>
                        <th scope="col" className="py-3 px-3 text-right text-xs font-semibold text-monday-text-muted uppercase tracking-wider">
                          Quantity
                        </th>
                        <th scope="col" className="py-3 px-3 text-left text-xs font-semibold text-monday-text-muted uppercase tracking-wider">
                          Status
                        </th>
                        <th scope="col" className="py-3 pl-3 pr-4 text-right text-xs font-semibold text-monday-text-muted uppercase tracking-wider sm:pr-6">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-monday-border-light bg-white">
                      {runs.map((run) => (
                        <tr key={run.id} className="hover:bg-monday-background-subtle/50 transition-colors">
                          <td className="whitespace-nowrap py-3 pl-4 pr-3 text-sm font-medium text-monday-text sm:pl-6">
                            {formatDate(run.bottlingDate)}
                          </td>
                          <td className="whitespace-nowrap py-3 px-3 text-sm text-monday-text-muted">
                            {run.lotNumber || '—'}
                          </td>
                          <td className="whitespace-nowrap py-3 px-3 text-sm text-monday-text-muted">
                            {run?.bottleSize ?? '—'}
                          </td>
                          <td className="whitespace-nowrap py-3 px-3 text-sm text-monday-text text-right">
                            {(run?.bottleCount ?? 0).toLocaleString()}
                          </td>
                          <td className="whitespace-nowrap py-3 px-3">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${run.status === 'Completed'
                                ? 'bg-emerald-500/15 text-emerald-700'
                                : run.status === 'Cancelled'
                                  ? 'bg-slate-200 text-slate-600'
                                  : 'bg-sky-500/15 text-sky-700'
                                }`}
                            >
                              {run.status}
                            </span>
                            {run.status === 'Completed' && bottlingIdsInInventory.has(run.id) && (
                              <span className="ml-1.5 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-monday-header text-monday-text-muted">
                                In inventory
                              </span>
                            )}
                          </td>
                          <td className="whitespace-nowrap py-3 pl-3 pr-4 text-right text-sm sm:pr-6">
                            <span className="inline-flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => openModal(run)}
                                className="inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full text-info-700 bg-info-50 hover:bg-info-100 focus:outline-none focus:ring-2 focus:ring-info-500 focus:ring-offset-1"
                              >
                                Edit
                              </button>
                              {isAdmin && (
                                <button
                                  type="button"
                                  onClick={() => handleDelete(run.id)}
                                  className="inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full text-error-700 bg-error-50 hover:bg-error-100 focus:outline-none focus:ring-2 focus:ring-error-500 focus:ring-offset-1"
                                >
                                  Delete
                                </button>
                              )}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            );
          })}
        </div>
      </div>

      <MobileModal isOpen={isModalOpen} onClose={closeModal} title={editingBottling ? 'Edit Bottling Run' : 'Add Bottling Run'} size="full">
        <div className="mobile-form-container">
          <Formik
            key={editingBottling?.id ?? `new-${prefillAgingId}`}
            initialValues={{
              agingId: editingBottling?.agingId ?? (editingBottling as any)?.aging_id ?? prefillAgingId ?? 0,
              batchId:
                editingBottling?.batchId
                ?? selectedAgingData?.batch_id
                ?? selectedAgingData?.batch?.id
                ?? (wineBatches.length > 0 ? wineBatches[0].id : 0),
              bottlingDate: editingBottling?.bottlingDate ? new Date(editingBottling.bottlingDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
              bottleSize: editingBottling?.bottleSize ?? '750ml',
              bottleCount: editingBottling?.bottleCount ?? 0,
              lotNumber: editingBottling?.lotNumber ?? '',
              responsibleUserId: editingBottling?.responsibleUserId ?? (users.length > 0 ? users[0].id : 0),
              status: editingBottling?.status ?? 'Planned',
              labelInfo: editingBottling?.labelInfo ?? {},
              notes: editingBottling?.notes ?? ''
            }}
            validationSchema={BottlingSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, setFieldValue, values }) => (
              <Form className="space-y-4">
                {!editingBottling ? (
                  <AgingProcessSelector
                    name="agingId"
                    value={values.agingId}
                    onChange={(agingId, agingData) => {
                      if (!agingData || agingId === 0) {
                        setFieldValue('agingId', 0);
                        setFieldValue('batchId', 0);
                        setSelectedAgingData(null);
                        return;
                      }
                      if (String(agingData.status) === 'Completed') {
                        setFieldValue('agingId', agingId);
                        setFieldValue('batchId', agingData.batch_id ?? agingData.batch?.id ?? 0);
                        setSelectedAgingData(agingData);
                        return;
                      }
                      // Active (or other incomplete): confirm before committing selection
                      applyAgingSelectionRef.current = (completed) => {
                        setFieldValue('agingId', completed.id);
                        setFieldValue('batchId', completed.batch_id ?? completed.batch?.id ?? 0);
                        setSelectedAgingData(completed);
                      };
                      setAgingConfirmMode('selector');
                      setAgingCompleteConfirmError(null);
                      setAgingCompleteConfirm(agingData);
                    }}
                    label="Aging Process"
                    required
                    filterStatus={['Active', 'Completed']}
                    refreshKey={agingSelectorRefreshKey}
                  />
                ) : (
                  <div className="space-y-1">
                    <label className="block text-sm font-normal text-monday-text-muted">Aging Process</label>
                    <p className="text-monday-text">
                      {editingBottling.aging?.vessel?.name ?? 'N/A'} – Batch {editingBottling.batchNumber ?? editingBottling.batchId}
                    </p>
                  </div>
                )}

                <FormDateInput name="bottlingDate" label="Bottling Date" required />

                <FormSelect
                  name="bottleSize"
                  label="Bottle Size"
                  options={bottleSizes.map((size) => ({
                    value: size.value,
                    label: size.label,
                  }))}
                  required
                />

                <FormField
                  name="bottleCount"
                  label="Quantity (Bottles)"
                  type="number"
                  placeholder="e.g., 300"
                  required
                />

                <FormField
                  name="lotNumber"
                  label="Lot Number"
                  type="text"
                  placeholder="e.g., LOT-2023-01"
                />

                <FormSelect
                  name="responsibleUserId"
                  label="Responsible User"
                  options={users.map((u) => ({
                    value: u.id,
                    label: `${u.firstName ?? u.first_name ?? ''} ${u.lastName ?? u.last_name ?? ''}`.trim() || u.email || 'Unknown',
                  }))}
                  required
                />

                <FormSelect
                  name="status"
                  label="Status"
                  options={[
                    { value: 'Planned', label: 'Planned' },
                    { value: 'Completed', label: 'Completed' },
                    { value: 'Cancelled', label: 'Cancelled' },
                  ]}
                />

                <FormTextarea
                  name="notes"
                  label="Notes"
                  rows={3}
                  placeholder="Additional notes about the bottling run"
                />

                <FormActions
                  onCancel={closeModal}
                  isSubmitting={isSubmitting}
                  submitText={editingBottling ? 'Update' : 'Save'}
                />
              </Form>
            )}
          </Formik>
        </div>
      </MobileModal>

      <ConfirmDialog
        isOpen={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false })}
        onConfirm={confirmDeleteBottling}
        title="Delete Bottling Run"
        message="Are you sure you want to delete this bottling run? This action cannot be undone."
        variant="danger"
      />

      <ConfirmDialog
        isOpen={!!addToInventoryPrompt}
        onClose={() => setAddToInventoryPrompt(null)}
        onConfirm={() => addToInventoryPrompt && handleCreateInventory(addToInventoryPrompt)}
        title="Add to Inventory?"
        message="This bottling run is completed. Would you like to add it to inventory now?"
        confirmText="Add to Inventory"
        cancelText="Not now"
        variant="info"
      />

      <ConfirmDialog
        isOpen={!!agingCompleteConfirm}
        onClose={closeAgingCompleteConfirm}
        onConfirm={() => { void handleConfirmAgingCompletedForBottling(); }}
        onDecline={() => setAgingCompleteConfirmError(AGING_MUST_COMPLETE_FOR_BOTTLING)}
        title={AGING_COMPLETED_CONFIRM_TITLE}
        message={AGING_COMPLETED_CONFIRM_QUESTION}
        confirmText="Yes"
        cancelText="No"
        variant="warning"
        isLoading={agingCompleteConfirmLoading}
        errorMessage={agingCompleteConfirmError || undefined}
      />
    </div>
  );
};

export default Bottling;
